import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { IUser } from '../../models/settings.models';
import { LanguageService } from 'src/app/config/language.service';
import { TranslateService } from '@ngx-translate/core';
import { MatSelectChange } from '@angular/material/select';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/core/app.state';
import { AuthService } from 'src/app/features/auth/authentication/services/auth.service';
import { BrowserNotificationPort } from 'src/app/features/notifications/services/browser-notification.port';
import { DesktopNotificationPreferenceService } from 'src/app/features/notifications/services/desktop-notification-preference.service';
import {
  deriveToggleState,
  ToggleState,
} from 'src/app/features/notifications/services/desktop-notification-toggle-state';
import * as settingsActions from '../../store/settings.actions';
import { getUserAvatarSrc } from 'src/app/shared/utils/user-color.util';

@Component({
  selector: 'app-profile-page',
  templateUrl: './profile-page.component.html',
  styleUrls: ['./profile-page.component.scss']
})
export class ProfilePageComponent implements OnInit {
  form: FormGroup;
  currentLanguage: string = '';
  avatarPreviewUrl: string | null = null;
  avatarFile: File | null = null;
  avatarFileName: string | null = null;
  avatarBase64: string | null = null;
  me$ = this.store.select(state => state.settings.me);
  // Starts unsupported so no card can flash before the real state is derived.
  desktopToggleState: ToggleState = 'unsupported';
  // Mirrors the control's position. A click flips the control itself, so the binding must be
  // driven through that same `true` for the reset back to `false` to register as a change and
  // reach the DOM.
  desktopChecked = false;
  constructor(
    private formBuilder: FormBuilder,
    private languageService: LanguageService,
    private translateService: TranslateService,
    private store: Store<AppState>,
    private authService: AuthService,
    private browserNotifications: BrowserNotificationPort,
    private desktopPreference: DesktopNotificationPreferenceService,
    private changeDetector: ChangeDetectorRef
  ) {
    this.form = this.formBuilder.group({
      name: [''],
      email: [''],
      phone: [''],
      language: [''],
      receive_emails: [false],
    });
  }

  ngOnInit(): void {
    this.store.dispatch(settingsActions.GetMe());
    this.me$.subscribe((me) => {
      if(me) {
        this.form.patchValue({
          name: me.name,
          email: me.email,
          phone: me.phone,
          language: this.currentLanguage,
          receive_emails: me.receive_emails,
        });
        // API may return a CDN URL or raw base64. Keep preview as a valid src.
        this.avatarPreviewUrl = me.image ? this.getImageSrc(me.image) : null;
        this.avatarBase64 = null; // only set when user uploads a new file
      }
    });
    this.currentLanguage = this.languageService.getCurrentLanguage();
    this.refreshDesktopToggleState();
  }

  // ── Desktop notifications ──────────────────────────────────────────────────

  private get currentUserId(): number | null {
    return this.authService.getCurrentUser()?.id ?? null;
  }

  private refreshDesktopToggleState(): void {
    this.desktopToggleState = deriveToggleState(
      this.browserNotifications.supported,
      this.browserNotifications.permission,
      this.desktopPreference.isEnabled(this.currentUserId),
    );

    this.desktopChecked = this.desktopToggleState === 'on';
  }

  async onDesktopNotificationsToggle(checked: boolean): Promise<void> {
    const userId = this.currentUserId;

    // The control flips itself on click while the binding still reads false, so the binding is
    // driven to that same value and flushed. Without the flush the whole handler settles inside
    // one change-detection turn, the binding never changes, and a rejected permission leaves the
    // control stuck in the on position.
    this.desktopChecked = checked;
    this.changeDetector.detectChanges();

    if (!checked) {
      this.desktopPreference.setEnabled(userId, false);
      this.refreshDesktopToggleState();
      return;
    }

    // Only an 'off' state may prompt: 'blocked' is terminal with no re-prompt API,
    // 'unsupported' has nothing to ask, and 'on' is already granted.
    if (this.desktopToggleState !== 'off') {
      this.refreshDesktopToggleState();
      return;
    }

    const permission = await this.browserNotifications.requestPermission();
    this.desktopPreference.setEnabled(userId, permission === 'granted');
    this.refreshDesktopToggleState();
  }

  private getImageSrc(image: string): string {
    return getUserAvatarSrc(image);
  }

  switchLanguage(event: MatSelectChange) {
    const lang = event.value;
    if (lang === this.translateService.currentLang) {
      return ;
    }
    localStorage.setItem('lang', lang);
    this.currentLanguage = lang;
    this.languageService.changeLanguage(lang);
    this.form.patchValue({
      language: lang,
    });
  }

  onAvatarFileChange(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.[0] ?? null;
    if (!file) {
      return;
    }

    if (!file.type?.startsWith('image/')) {
      this.clearAvatar(input ?? undefined);
      return;
    }

    this.avatarFile = file;
    this.avatarFileName = file.name;

    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = typeof reader.result === 'string' ? reader.result : null;
      this.avatarPreviewUrl = dataUrl;
      this.avatarBase64 = dataUrl?.includes(',') ? dataUrl.split(',')[1] : null;
    };
    reader.readAsDataURL(file);
  }

  clearAvatar(input?: HTMLInputElement, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.avatarFile = null;
    this.avatarFileName = null;
    this.avatarPreviewUrl = null;
    this.avatarBase64 = null;
    if (input) {
      input.value = '';
    }
  }

  save() {
    const userPayload: IUser = { ...this.form.value };
    if (this.avatarBase64) {
      userPayload.image = this.avatarBase64;
    }
    this.store.dispatch(settingsActions.UpdateMe({ user: userPayload }));
  }
}
